import { BleClient, type BleDevice } from '@capacitor-community/bluetooth-le';
import { invertRgba, restoreQrAreas, type QrArea } from './qrPolarity';

/**
 * MarkLife P15 speaks the L11 protocol over Bluetooth LE, not Bluetooth
 * Classic.  Its payload is an ESC/POS `GS v 0` raster, wrapped in a short
 * P15-specific start/end sequence and sent with credit-based flow control.
 */

const SERVICE = '0000ff00-0000-1000-8000-00805f9b34fb';
const RX = '0000ff01-0000-1000-8000-00805f9b34fb';
const TX = '0000ff02-0000-1000-8000-00805f9b34fb';
const CONTROL = '0000ff03-0000-1000-8000-00805f9b34fb';
const CHUNK_SIZE = 95;
const PACKET_DELAY_MS = 30;
const POSTFEED_DELAY_MS = 80;
const LABEL_SETTLE_MS = 1200;
const PREAMBLE_SIZE = 23;
const POSTFEED_SIZE = 6;

export type PrinterFailure = 'unsupported' | 'insecure' | 'refused' | 'closed' | 'printer_silent' | 'unknown';

export interface PrinterInfo { model: string; density: number }

let device: BleDevice | null = null;
let printerInfo: PrinterInfo | null = null;
let credits = 0;
let creditWaiter: (() => void) | null = null;

export function support(): PrinterFailure | 'ok' {
  if (!window.isSecureContext) return 'insecure';
  // The Capacitor plugin uses Android BLE in the app and Web Bluetooth in a
  // desktop Chrome/Edge session.  Do not offer a button where neither exists.
  if (!('bluetooth' in navigator) && !('Capacitor' in window)) return 'unsupported';
  return 'ok';
}

/** The active connection survives closing a label dialog, unlike its UI state. */
export function current(): PrinterInfo | null {
  return printerInfo;
}

function bytes(value: DataView): Uint8Array {
  return new Uint8Array(value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength));
}

function addCredits(value: DataView): void {
  const received = bytes(value);
  if (received[0] === 0x01 && received[1]) {
    credits += received[1];
    creditWaiter?.();
    creditWaiter = null;
  }
}

export async function connect(): Promise<PrinterInfo> {
  const state = support();
  if (state !== 'ok') throw new Error(state);

  await disconnect();
  await BleClient.initialize({ androidNeverForLocation: true });

  let opened: BleDevice | null = null;
  try {
    opened = await BleClient.requestDevice({ namePrefix: 'P15', optionalServices: [SERVICE] });
    await BleClient.connect(opened.deviceId, () => { device = null; printerInfo = null; credits = 0; });
    await BleClient.startNotifications(opened.deviceId, SERVICE, RX, () => undefined);
    await BleClient.startNotifications(opened.deviceId, SERVICE, CONTROL, addCredits);
  } catch {
    await BleClient.disconnect(opened?.deviceId ?? '').catch(() => undefined);
    throw new Error('refused');
  }

  if (!opened) throw new Error('refused');
  device = opened;
  credits = 0;
  printerInfo = { model: opened.name || 'P15', density: 2 };
  return printerInfo;
}

export async function disconnect(): Promise<void> {
  const open = device;
  device = null;
  printerInfo = null;
  credits = 0;
  creditWaiter?.();
  creditWaiter = null;
  if (open) await BleClient.disconnect(open.deviceId).catch(() => undefined);
}

async function waitForCredit(): Promise<void> {
  if (credits > 0) { credits -= 1; return; }

  // Older P15 firmware occasionally loses its first credit notification.  The
  // vendor app resumes with one packet after a second, which is safe here too:
  // every following packet still waits for an explicit grant.
  await new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, 1000);
    creditWaiter = () => { clearTimeout(timer); resolve(); };
  });
  if (credits > 0) credits -= 1;
}

async function write(data: Uint8Array): Promise<void> {
  const open = device;
  if (!open) throw new Error('closed');
  await BleClient.writeWithoutResponse(open.deviceId, SERVICE, TX, new DataView(data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength)));
}

async function send(data: Uint8Array): Promise<void> {
  for (let offset = 0; offset < data.length; offset += CHUNK_SIZE) {
    await waitForCredit();
    const end = Math.min(offset + CHUNK_SIZE, data.length);
    await write(data.subarray(offset, end));
    // Credits describe buffer space, not thermal-head readiness. Keep the
    // proven inter-packet gap even when the printer grants several at once.
    if (end < data.length) await pause(PACKET_DELAY_MS);
  }
}

function pause(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

interface Raster { data: Uint8Array; width: number; height: number }

/** Inversion is done after rotation and packing, so black/white polarity is
 * identical on every P15 firmware. */
export function invertRaster(data: Uint8Array): Uint8Array {
  return data.map((byte) => byte ^ 0xff);
}

/**
 * The P15 feeds the long edge of the label. The server preview is laid out
 * across that edge, so rotate it before turning pixels into a printer raster.
 */
export function rasterDimensions(sourceWidth: number, sourceHeight: number): Pick<Raster, 'width' | 'height'> {
  return { width: Math.ceil(sourceHeight / 8) * 8, height: sourceWidth };
}

async function raster(
  bitmap: Blob,
  inverted: boolean,
  qrAreas: readonly QrArea[] = [],
  mediaWidthMm?: number,
): Promise<Raster> {
  const image = await createImageBitmap(bitmap);
  const source = document.createElement('canvas');
  source.width = image.width; source.height = image.height;
  const sourceContext = source.getContext('2d', { willReadFrequently: true });
  if (!sourceContext) throw new Error('unknown');
  sourceContext.fillStyle = '#fff'; sourceContext.fillRect(0, 0, source.width, source.height);
  sourceContext.drawImage(image, 0, 0);
  // Preserve QR polarity before rotation. Rotating the protected raster keeps
  // both the code and its white quiet zone aligned with the label.
  const protectedQr = inverted && qrAreas.length > 0 && (Boolean(mediaWidthMm) || qrAreas.some((area) => area.unit === 'px'));
  if (protectedQr) {
    const pixels = sourceContext.getImageData(0, 0, source.width, source.height);
    const normal = new Uint8ClampedArray(pixels.data);
    invertRgba(pixels.data);
    restoreQrAreas(pixels.data, normal, source.width, source.height, qrAreas, mediaWidthMm ? source.width / mediaWidthMm : 1);
    sourceContext.putImageData(pixels, 0, 0);
  }
  const { width, height } = rasterDimensions(image.width, image.height);
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('unknown');
  context.fillStyle = '#fff'; context.fillRect(0, 0, width, height);
  context.translate(image.height, 0);
  context.rotate(Math.PI / 2);
  context.drawImage(source, 0, 0);
  const pixels = context.getImageData(0, 0, width, height).data;
  const data = new Uint8Array((width / 8) * height);
  for (let y = 0; y < height; y += 1) for (let x = 0; x < width; x += 1) {
    const pixel = (y * width + x) * 4;
    if ((pixels[pixel] ?? 255) + (pixels[pixel + 1] ?? 255) + (pixels[pixel + 2] ?? 255) < 384) {
      const byte = y * (width / 8) + Math.floor(x / 8);
      data[byte] = (data[byte] ?? 0) | (0x80 >> (x % 8));
    }
  }
  return { data: inverted && !protectedQr ? invertRaster(data) : data, width, height };
}

/** The complete L11 job for one die-cut label. Exported for byte-level tests. */
export function printJob(image: Raster, density: number): Uint8Array {
  const bytesPerRow = image.width / 8;
  const head = new Uint8Array([
    0x1f, 0x70, 0x02, density,
    ...new Uint8Array(15),
    0x10, 0xff, 0xf1, 0x02,
    0x1d, 0x76, 0x30, 0,
    bytesPerRow & 255, bytesPerRow >> 8, image.height & 255, image.height >> 8,
  ]);
  const tail = new Uint8Array([0x1d, 0x0c, 0x10, 0xff, 0xf1, 0x45]);
  const job = new Uint8Array(head.length + image.data.length + tail.length);
  job.set(head); job.set(image.data, head.length); job.set(tail, head.length + image.data.length);
  return job;
}

/** Keep control commands out of the final raster packet. Some P15 firmware
 * consumes a merged trailer as bitmap data, then receives the next job while
 * still feeding the current label. */
export function splitPrintJob(job: Uint8Array): {
  preamble: Uint8Array;
  raster: Uint8Array;
  postfeed: Uint8Array;
} {
  if (job.length <= PREAMBLE_SIZE + POSTFEED_SIZE) throw new Error('unknown');
  return {
    preamble: job.slice(0, PREAMBLE_SIZE),
    raster: job.slice(PREAMBLE_SIZE, -POSTFEED_SIZE),
    postfeed: job.slice(-POSTFEED_SIZE),
  };
}

export async function print(bitmap: Blob, options: { copies: number; density: number; inverted?: boolean; qrAreas?: QrArea[]; mediaWidthMm?: number }): Promise<void> {
  const image = await raster(bitmap, options.inverted ?? false, options.qrAreas, options.mediaWidthMm);
  const job = splitPrintJob(printJob(image, options.density));
  for (let copy = 0; copy < options.copies; copy += 1) {
    await send(job.preamble);
    await send(job.raster);
    // Position-to-gap and stop must arrive after the raster, never as its tail.
    await pause(POSTFEED_DELAY_MS);
    await send(job.postfeed);
    // The printer can acknowledge bytes before its motor reaches the gap.
    if (copy + 1 < options.copies) await pause(LABEL_SETTLE_MS);
  }
}
